import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ZoomInIcon, XIcon } from 'lucide-react';
import { useBackNavigation } from '../hooks/useBackNavigation';
export function CoLiving() {
  const goBack = useBackNavigation('/architecture');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  return <div className="w-full min-h-screen bg-white" data-id="element-819">
      {/* Lightbox */}
      {selectedImage && <div className="fixed inset-0 z-50 bg-stone-900/95 flex items-center justify-center p-6" onClick={() => setSelectedImage(null)} data-id="element-820">
          <button className="absolute top-6 right-6 text-white hover:text-stone-300 transition-colors" onClick={() => setSelectedImage(null)} data-id="element-821">
            <XIcon className="w-8 h-8" data-id="element-822" />
          </button>
          <img src={selectedImage} alt="Enlarged view" className="max-w-full max-h-full object-contain" onClick={e => e.stopPropagation()} data-id="element-823" />
        </div>}
      {/* Header */}
      <header className="py-8 px-6 bg-white border-b border-stone-200 sticky top-0 z-40" data-id="element-824">
        <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-825">
          <button onClick={goBack} className="inline-flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors group" data-id="element-826">
            <ArrowLeftIcon className="w-5 h-5 group-hover:-translate-x-1 transition-transform" data-id="element-827" />
            <span className="font-light" data-id="element-828">Back</span>
          </button>
          <h1 className="text-2xl font-light text-stone-900" data-id="element-829">Maria Elina</h1>
        </div>
      </header>
      {/* Hero Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-stone-50 via-emerald-50/30 to-stone-50" data-id="element-830">
        <div className="max-w-5xl mx-auto" data-id="element-831">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-832">
            <p className="text-emerald-700 font-light tracking-wide uppercase mb-4" data-id="element-833">
              Interior Architecture • Adaptive Re-Use
            </p>
            <h1 className="text-4xl md:text-6xl font-light text-stone-900 mb-6" data-id="element-834">
              Co-Living
            </h1>
            <p className="text-xl text-stone-600 font-light mb-8" data-id="element-835">
              Transforming an underutilized office building into affordable,
              community-focused living spaces
            </p>
            <div className="space-y-4 text-stone-700 font-light" data-id="element-836">
              <p className="text-lg" data-id="element-837">
                <span className="font-normal text-stone-900" data-id="element-838">Role:</span>{' '}
                Interior Architect, Spatial Designer
              </p>
              <p className="text-lg" data-id="element-839">
                <span className="font-normal text-stone-900" data-id="element-840">Location:</span>{' '}
                Dean Park Crescent, Bournemouth
              </p>
              <p className="text-lg" data-id="element-841">
                <span className="font-normal text-stone-900" data-id="element-842">Type:</span>{' '}
                Academic Project, Adaptive Reuse, Community Design
              </p>
              <p className="text-lg" data-id="element-843">
                <span className="font-normal text-stone-900" data-id="element-844">
                  Design Philosophy:
                </span>{' '}
                Eclectic and Bohemian embracing reduce-reuse-recycle ideology
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Floor Plans */}
      <section className="py-20 px-6 bg-white" data-id="element-845">
        <div className="max-w-7xl mx-auto" data-id="element-846">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-847">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-848">
              Building Organization
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-849">
              The adaptive reuse design maximizes the existing building
              structure, creating distinct zones for living, working, and
              community spaces across multiple floors.
            </p>
            <div className="grid md:grid-cols-2 gap-6" data-id="element-850">
              {/* First Image */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/co-living_ready/image%20(35).png')} data-id="element-851">
                <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-852">
                  <img src="https://storage.yandexcloud.net/maria-images/images/co-living_ready/image%20(35).png" alt="Floor Plans Overview" className="w-full h-auto" data-id="element-853" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-854">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-855" />
                  </div>
                </div>
                <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-856">
                  Axonometric floor plan views showing living units and communal
                  spaces
                </p>
              </motion.div>
              {/* Second Image */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/co-living_ready/image%20(34).png')} data-id="element-857">
                <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-858">
                  <img src="https://storage.yandexcloud.net/maria-images/images/co-living_ready/image%20(34).png" alt="Exterior View" className="w-full h-auto" data-id="element-859" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-860">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-861" />
                  </div>
                </div>
                <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-862">
                  Exterior view showing entrance ideation
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Building Study */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-863">
        <div className="max-w-7xl mx-auto" data-id="element-864">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-865">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-866">
              Building Study
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-867">
              The office structure facilitates 4 distinct zones visible through
              building sections - regular living apartments (floors G to 5),
              suites (floor 6), entertainment basement and a social rooftop.
            </p>
            <div className="grid md:grid-cols-2 gap-6" data-id="element-868">
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/co-living_ready/sections.png')} data-id="element-869">
                <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-870">
                  <img src="https://storage.yandexcloud.net/maria-images/images/co-living_ready/sections.png" alt="Full Building Sections" className="w-full h-auto" data-id="element-871" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-872">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-873" />
                  </div>
                </div>
                <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-874">
                  Full building sections
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/co-living_ready/Section%20(1).png')} data-id="element-875">
                <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-876">
                  <img src="https://storage.yandexcloud.net/maria-images/images/co-living_ready/Section%20(1).png" alt="Detailed Building Sections" className="w-full h-auto" data-id="element-877" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-878">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-879" />
                  </div>
                </div>
                <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-880">
                  Detailed sections showing the basement zones
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Design Philosophy */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-881">
        <div className="max-w-5xl mx-auto" data-id="element-882">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-883">
            <h2 className="text-3xl font-light text-stone-900 mb-6" data-id="element-884">
              Trendy yet Timeless
            </h2>
            <div className="space-y-6 text-lg text-stone-600 font-light leading-relaxed mb-12" data-id="element-885">
              <p data-id="element-886">
                Eclectic and Bohemian styles were chosen to create spaces that
                embrace a mixture of furnishing styles, perfectly aligning with
                the reduce-reuse-recycle ideology. This approach celebrates
                sustainability while creating warm, inviting environments.
              </p>
              <p data-id="element-887">
                The design creates a new model of living that brings together
                residents with similar aspirations into one communal family. It
                is aimed at active residents in remote employment who want to
                live in central Bournemouth without compromising comfort or
                finances.
              </p>
            </div>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/co-living_ready/image%20(22).png')} data-id="element-888">
              <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-889">
                <img src="https://storage.yandexcloud.net/maria-images/images/co-living_ready/image%20(22).png" alt="Material Studies and Design Aesthetic" className="w-full h-auto" data-id="element-890" />
                <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-891">
                  <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-892" />
                </div>
              </div>
              <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-893">
                Material studies showcasing natural materials, repurposed
                furniture, and the eclectic bohemian aesthetic
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Working Spaces */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-894">
        <div className="max-w-7xl mx-auto" data-id="element-895">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-896">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-897">
              Working and Enjoying
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-898">
              Each living floor is equipped with dedicated working spaces and
              innovative "working pods" that ascend between floors, providing
              flexible environments for remote professionals.
            </p>
            <div className="grid grid-cols-1 gap-6 max-w-4xl mx-auto" data-id="element-899">
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/co-living_ready/image%20(21).png')} data-id="element-900">
                <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-901">
                  <img src="https://storage.yandexcloud.net/maria-images/images/co-living_ready/image%20(21).png" alt="Working Pods" className="w-full h-auto" data-id="element-902" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-903">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-904" />
                  </div>
                </div>
                <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-905">
                  Working pods integrated with greenery, creating inspiring
                  spaces between floors
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/co-living_ready/interior%20final%20projection%20living%20room.png')} data-id="element-906">
                <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-907">
                  <img src="https://storage.yandexcloud.net/maria-images/images/co-living_ready/interior%20final%20projection%20living%20room.png" alt="Rooftop Space" className="w-full h-auto" data-id="element-908" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-909">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-910" />
                  </div>
                </div>
                <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-911">
                  Residential floors combining work and social areas
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Basement Oasis */}
      <section className="py-20 px-6 bg-white" data-id="element-912">
        <div className="max-w-7xl mx-auto" data-id="element-913">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-914">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-915">
              The Basement Oasis
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-916">
              A unique feature made possible by the daylight re-directing
              technology, allowing natural sunlight to reach the plants below
              and create a lush, green communal space.
            </p>
            <div className="grid md:grid-cols-2 gap-6" data-id="element-917">
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/co-living_ready/image%20(38).png')} data-id="element-918">
                <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-919">
                  <img src="https://storage.yandexcloud.net/maria-images/images/co-living_ready/image%20(38).png" alt="Basement Corridor" className="w-full h-auto" data-id="element-920" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-921">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-922" />
                  </div>
                </div>
                <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-923">
                  Corridor surrounding the oasis leading to entertainment areas
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/co-living_ready/cafe%20basement%20view%203d.png')} data-id="element-924">
                <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-925">
                  <img src="https://storage.yandexcloud.net/maria-images/images/co-living_ready/cafe%20basement%20view%203d.png" alt="Basement Cafe" className="w-full h-auto" data-id="element-926" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-927">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-928" />
                  </div>
                </div>
                <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-929">
                  The basement cafe featuring green decor and mixed upcycled
                  furniture
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Project Impact */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-930">
        <div className="max-w-5xl mx-auto" data-id="element-931">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-932">
            <h2 className="text-3xl font-light text-stone-900 mb-8" data-id="element-933">
              A New Model of Living
            </h2>
            <div className="space-y-6 text-lg text-stone-600 font-light leading-relaxed" data-id="element-934">
              <p data-id="element-935">
                This co-living project addresses Bournemouth's lack of
                affordable housing for young professionals while championing
                sustainable design practices through material reuse and adaptive
                building transformation.
              </p>
              <p data-id="element-936">
                By integrating nature throughout the building - from the
                basement oasis to rooftop gardens - the design creates a
                holistic living environment that supports both productivity and
                well-being, fostering a genuine sense of community among
                residents.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}